import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { getEvents, updateEvent, deleteEvent } from '../api';
import type { Vehicle, VehicleEvent, VatStatus } from '../types';
import { kmToDisplay, displayToKm, distanceUnitLabel, litersToDisplay, displayToLiters, volumeUnitLabel } from '../utils/units';
import PriceInput from './PriceInput';
import UserSelect from './UserSelect';
import QuickEndTime from './QuickEndTime';

// Konvertér brugerinput ("1.820,29" eller "1820.29") til ren tal-streng til backend
const parseAmount = (v: string): string | null => {
  if (!v) return null;
  const n = parseFloat(v.replace(/\./g, '').replace(',', '.'));
  return isFinite(n) ? String(n) : null;
};

interface Props {
  vehicle: Vehicle;
  onRegister?: () => void;
  limit?: number;
  openEventType?: 'inspection' | 'maintenance' | 'checkup';
  openEventId?: number;
  onClose?: () => void;  // hvis sat, kaldes når brugeren lukker detalje-/edit-view i stedet for at gå tilbage til intern liste
}

type HistoryView = 'list' | 'detail' | 'edit';
type FilterKey = 'all' | 'inspection' | 'maintenance_planned' | 'maintenance_unplanned' | 'checkup';

function calcMinutes(start: string, end: string): string {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? String(diff) : '';
}

function addYears(date: string, years: number): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  return `${y + years}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

export default function EventHistory({ vehicle, onRegister: _onRegister, limit, openEventType, openEventId, onClose }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const vehicleId = vehicle.id!;

  const [events, setEvents] = useState<VehicleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<HistoryView>('list');
  const [selected, setSelected] = useState<VehicleEvent | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // --- Edit state: inspection ---
  const [eInsType, setEInsType] = useState<'periodic' | 'recheck' | 'registration'>('periodic');
  const [eInsDate, setEInsDate] = useState('');
  const [eInsBlocked, setEInsBlocked] = useState(false);
  const [eInsStartTime, setEInsStartTime] = useState('');
  const [eInsEndTime, setEInsEndTime] = useState('');
  const [eInsPerformedBy, setEInsPerformedBy] = useState('');
  const [eInsKm, setEInsKm] = useState('');
  const [eInsResult, setEInsResult] = useState<'passed' | 'conditional' | 'failed'>('passed');
  const [eInsCost, setEInsCost] = useState('');
  const [eInsVatStatus, setEInsVatStatus] = useState<VatStatus>('full');
  const [eInsVatAmount, setEInsVatAmount] = useState('');
  const [eInsTime, setEInsTime] = useState('');
  const [eInsNextDue, setEInsNextDue] = useState('');
  const [eInsNotes, setEInsNotes] = useState('');

  // --- Edit state: maintenance ---
  const [eMType, setEMType] = useState<'planned' | 'unplanned'>('planned');
  const [eMDate, setEMDate] = useState('');
  const [, setEMDesc] = useState('');
  const [eMKm, setEMKm] = useState('');
  const [eMCost, setEMCost] = useState('');
  const [eMVatStatus, setEMVatStatus] = useState<VatStatus>('full');
  const [eMVatAmount, setEMVatAmount] = useState('');
  const [eMTime, setEMTime] = useState('');
  const [eMNextDate, setEMNextDate] = useState('');
  const [eMNextKm, setEMNextKm] = useState('');
  const [, setEMWorkshop] = useState('');
  const [eMNotes, setEMNotes] = useState('');
  // Værksted (unplanned) extras
  const [eMStartTime, setEMStartTime] = useState('');
  const [eMEndTime, setEMEndTime] = useState('');
  const [eMPerformedBy, setEMPerformedBy] = useState('');
  const [eMBlocked, setEMBlocked] = useState(false);

  // --- Edit state: checkup ---
  const [eCDate, setECDate] = useState('');
  const [eCBlocked, setECBlocked] = useState(false);
  const [eCStartTime, setECStartTime] = useState('');
  const [eCEndTime, setECEndTime] = useState('');
  const [eCBy, setECBy] = useState('');
  // Der skal altid vælges en medarbejder (Kennith 2026-10-14) — advarsel ved gem uden
  const [employeeMissing, setEmployeeMissing] = useState(false);
  const [eCWashed, setECWashed] = useState(false);
  const [eCVacuumed, setECVacuumed] = useState(false);
  const [eCCleaned, setECCleaned] = useState(false);
  const [eCExtCond, setECExtCond] = useState<number | ''>('');
  const [eCIntCond, setECIntCond] = useState<number | ''>('');
  const [eCTyre, setECTyre] = useState(false);
  const [eCWasherFluid, setECWasherFluid] = useState(false);
  const [eCWasherFill, setECWasherFill] = useState(false);
  const [eCOilChecked, setECOilChecked] = useState(false);
  const [eCOilAdded, setECOilAdded] = useState(false);
  const [eCWindowClean, setECWindowClean] = useState(false);
  const [eCOilLitres, setECOilLitres] = useState('');
  const [eCAdblue, setECAdblue] = useState(false);
  const [eCAdblueKm, setECAdblueKm] = useState('');
  const [eCControlOk, setECControlOk] = useState(false);
  const [eCKm, setECKm] = useState('');
  const [eCTime, setECTime] = useState('');
  const [eCNotes, setECNotes] = useState('');

  // "Kontrolleret, alt OK": ryd øvrige afkrydsninger og sæt tidsforbrug til 5 min
  const applyECControlOk = (checked: boolean) => {
    setECControlOk(checked);
    if (checked) {
      setECWashed(false); setECVacuumed(false); setECCleaned(false); setECWindowClean(false);
      setECTyre(false); setECWasherFluid(false); setECWasherFill(false);
      setECOilChecked(false); setECOilAdded(false); setECAdblue(false);
      setECOilLitres(''); setECAdblueKm('');
      setECExtCond(''); setECIntCond('');
      setECTime('5');
    }
  };

  const load = async () => {
    setLoading(true);
    const data = await getEvents(vehicleId);
    data.sort((a: VehicleEvent, b: VehicleEvent) => String(b.date).slice(0, 10) > String(a.date).slice(0, 10) ? 1 : -1);
    setEvents(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [vehicleId]);

  // Auto-open a specific event if requested via props (e.g. from expenses page)
  useEffect(() => {
    if (loading || !openEventId || !openEventType) return;
    const target = events.find(e => e.event_type === openEventType && e.id === openEventId);
    if (target) {
      setSelected(target);
      setView('detail');
    }
  }, [loading, events, openEventType, openEventId]);

  // --- Helpers ---
  const filterKey = (e: VehicleEvent): FilterKey => {
    if (e.event_type === 'maintenance') return e.type === 'planned' ? 'maintenance_planned' : 'maintenance_unplanned';
    return e.event_type as FilterKey;
  };

  const filterLabel = (key: FilterKey) => {
    if (key === 'all') return t('events.all');
    if (key === 'inspection') return t('events.inspection');
    if (key === 'maintenance_planned') return t('events.planned');
    if (key === 'maintenance_unplanned') return t('events.unplanned');
    return t('events.checkup');
  };

  const eventLabel = (e: VehicleEvent) => {
    if (e.event_type === 'inspection') {
      if (e.inspection_type) return t(`events.inspection_type_${e.inspection_type}`);
      return t('events.inspection');
    }
    if (e.event_type === 'maintenance') return t(`events.${e.type === 'planned' ? 'planned' : 'unplanned'}`);
    return t('events.checkup');
  };

  const availableFilters: FilterKey[] = ['all', ...([...new Set(events.map(filterKey))] as FilterKey[])];

  const matchesSearch = (e: VehicleEvent) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const fields = [
      e.date, e.result, e.inspection_type, e.description, e.notes,
      e.workshop, e.performed_by,
      e.cost != null ? String(e.cost) : null,
      e.km_reading != null ? String(e.km_reading) : null,
      e.next_due_date,
      eventLabel(e),
    ];
    return fields.some(f => f && f.toLowerCase().includes(q));
  };

  const filteredEvents = events
    .filter(e => filter === 'all' || filterKey(e) === filter)
    .filter(matchesSearch)
    .sort((a, b) => String(b.date).slice(0, 10) > String(a.date).slice(0, 10) ? 1 : -1);

  const fmtDate = (d?: string) => {
    if (!d) return '—';
    const s = d.slice(0, 10);
    const [y, m, day] = s.split('-');
    if (settings.dateFormat === 'MM/DD/YYYY') return `${m}/${day}/${y}`;
    if (settings.dateFormat === 'YYYY-MM-DD') return s;
    return `${day}/${m}/${y}`;
  };
  const fmt = fmtDate;
  const fmtTime = (t?: string) => t ? t.slice(0, 5) : null;
  const fmtTimeRange = (e: VehicleEvent) => {
    const s = fmtTime(e.start_time);
    const end = fmtTime(e.end_time);
    if (s && end) return `${s} - ${end}`;
    if (s) return s;
    return null;
  };
  const fmtNum = (n?: number | null) => n != null ? n.toLocaleString() : '—';
  const fmtAmount = (n?: number | null) => n != null ? formatNumber(Number(n), settings.numberFormat) : '—';
  const distUnit = settings.distanceUnit;
  const distLabel = distanceUnitLabel(distUnit);
  const fmtDist = (km?: number | null) => km != null ? `${fmtNum(kmToDisplay(km, distUnit))} ${distLabel}` : '—';
  // Konverter km fra DB til display-enhed (til input-felter)
  const kmToInput = (km: number | null | undefined) => km != null ? String(kmToDisplay(km, distUnit)) : '';
  // Konverter display-enhed tilbage til km (ved gem)
  const toKm = (val: string) => val ? displayToKm(+val, distUnit) : null;
  const fuelUnit = settings.fuelUnit;
  const fuelLabel = volumeUnitLabel(fuelUnit);
  const litersToInput = (l: number | null | undefined) => l != null ? String(litersToDisplay(l, fuelUnit)) : '';
  const toLiters = (val: string) => val ? displayToLiters(+val, fuelUnit) : null;

  // --- Init edit state from selected event ---
  const initEdit = (e: VehicleEvent) => {
    if (e.event_type === 'inspection') {
      setEInsType((e.inspection_type ?? 'periodic') as 'periodic' | 'recheck' | 'registration');
      setEInsDate(e.date.slice(0, 10));
      setEInsBlocked(!!e.blocked_for_rental);
      setEInsStartTime(e.start_time ? e.start_time.slice(0, 5) : '');
      setEInsEndTime(e.end_time ? e.end_time.slice(0, 5) : '');
      setEInsPerformedBy(e.performed_by_user_id ? String(e.performed_by_user_id) : ''); setEmployeeMissing(false);
      setEInsKm(kmToInput(e.km_reading));
      setEInsResult((e.result ?? 'passed') as 'passed' | 'conditional' | 'failed');
      setEInsCost(e.cost != null ? formatNumber(e.cost, settings.numberFormat) : '');
      setEInsVatStatus((e.vat_status as VatStatus) ?? 'full');
      setEInsVatAmount(e.vat_amount != null ? formatNumber(e.vat_amount, settings.numberFormat) : '');
      setEInsTime(e.time_spent != null ? String(e.time_spent) : '');
      setEInsNextDue(e.next_due_date?.slice(0, 10) ?? '');
      setEInsNotes(e.notes ?? '');
    } else if (e.event_type === 'maintenance') {
      setEMType((e.type ?? 'planned') as 'planned' | 'unplanned');
      setEMDate(e.date.slice(0, 10));
      setEMDesc(e.description ?? '');
      setEMKm(kmToInput(e.km_reading));
      setEMCost(e.cost != null ? formatNumber(e.cost, settings.numberFormat) : '');
      setEMVatStatus((e.vat_status as VatStatus) ?? 'full');
      setEMVatAmount(e.vat_amount != null ? formatNumber(e.vat_amount, settings.numberFormat) : '');
      setEMTime(e.time_spent != null ? String(e.time_spent) : '');
      setEMNextDate(e.next_due_date?.slice(0, 10) ?? '');
      setEMNextKm(kmToInput(e.next_due_km));
      setEMWorkshop(e.workshop ?? '');
      setEMNotes(e.notes ?? '');
      setEMStartTime(e.start_time ? e.start_time.slice(0, 5) : '');
      setEMEndTime(e.end_time ? e.end_time.slice(0, 5) : '');
      setEMPerformedBy(e.performed_by_user_id ? String(e.performed_by_user_id) : ''); setEmployeeMissing(false);
      setEMBlocked(!!e.blocked_for_rental);
    } else {
      setECDate(e.date.slice(0, 10));
      setECBlocked(!!e.blocked_for_rental);
      setECStartTime(e.start_time ? e.start_time.slice(0, 5) : '');
      setECEndTime(e.end_time ? e.end_time.slice(0, 5) : '');
      setECBy(e.performed_by_user_id ? String(e.performed_by_user_id) : ''); setEmployeeMissing(false);
      setECWashed(!!e.washed);
      setECVacuumed(!!e.vacuumed);
      setECCleaned(!!e.cleaned);
      setECExtCond(e.exterior_condition ?? '');
      setECIntCond(e.interior_condition ?? '');
      setECTyre(!!e.tyre_pressure);
      setECWasherFluid(!!e.washer_fluid);
      setECWasherFill(!!e.washer_fill);
      setECOilChecked(!!e.oil_checked);
      setECOilAdded(!!e.oil_added);
      setECWindowClean(!!e.window_clean);
      setECOilLitres(litersToInput(e.oil_added_litres));
      setECAdblue(!!e.adblue_fill);
      setECAdblueKm(kmToInput(e.adblue_fill_km));
      setECControlOk(!!e.control_ok);
      setECKm(kmToInput(e.km_reading));
      setECTime(e.time_spent != null ? String(e.time_spent) : '');
      setECNotes(e.notes ?? '');
    }
  };

  const handleView = (e: VehicleEvent) => {
    setSelected(e);
    setView('detail');
  };

  const closeOrList = () => {
    if (onClose) onClose();
    else { setView('list'); setSelected(null); }
  };

  const handleEdit = () => {
    if (selected) {
      initEdit(selected);
      setView('edit');
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    const employee = selected.event_type === 'inspection' ? eInsPerformedBy : selected.event_type === 'maintenance' ? eMPerformedBy : eCBy;
    if (!employee) { setEmployeeMissing(true); return; }
    setSaving(true);
    if (selected.event_type === 'inspection') {
      await updateEvent('inspection', selected.id, {
        date: eInsDate, blocked_for_rental: eInsBlocked,
        start_time: eInsStartTime || null, end_time: eInsEndTime || null,
        performed_by_user_id: Number(eInsPerformedBy) || null, km_reading: toKm(eInsKm),
        result: eInsResult, inspection_type: eInsType,
        notes: eInsNotes || null,
        cost: parseAmount(eInsCost), vat_status: eInsVatStatus, vat_amount: parseAmount(eInsVatAmount),
        time_spent: eInsTime || null,
        next_due_date: eInsType === 'periodic' ? (eInsNextDue || null) : null,
      });
    } else if (selected.event_type === 'maintenance') {
      await updateEvent('maintenance', selected.id, eMType === 'unplanned' ? {
        vehicle_id: vehicleId, type: 'unplanned',
        date: eMDate, start_time: eMStartTime || null, end_time: eMEndTime || null,
        performed_by_user_id: Number(eMPerformedBy) || null, time_spent: eMTime || null,
        cost: parseAmount(eMCost), vat_status: eMVatStatus, vat_amount: parseAmount(eMVatAmount),
        notes: eMNotes || null, blocked_for_rental: eMBlocked,
      } : {
        vehicle_id: vehicleId, type: 'planned',
        date: eMDate, start_time: eMStartTime || null, end_time: eMEndTime || null,
        blocked_for_rental: eMBlocked, km_reading: toKm(eMKm),
        cost: parseAmount(eMCost), vat_status: eMVatStatus, vat_amount: parseAmount(eMVatAmount),
        performed_by_user_id: Number(eMPerformedBy) || null, time_spent: eMTime || null,
        next_due_date: eMNextDate || null, next_due_km: toKm(eMNextKm), notes: eMNotes || null,
      });
    } else {
      await updateEvent('checkup', selected.id, {
        vehicle_id: vehicleId, date: eCDate,
        start_time: eCStartTime || null, end_time: eCEndTime || null,
        blocked_for_rental: eCBlocked, performed_by_user_id: Number(eCBy) || null,
        washed: eCWashed, vacuumed: eCVacuumed, cleaned: eCCleaned,
        exterior_condition: eCExtCond || null, interior_condition: eCIntCond || null,
        tyre_pressure: eCTyre, washer_fluid: eCWasherFluid, washer_fill: eCWasherFill,
        oil_checked: eCOilChecked, oil_added: eCOilAdded, oil_added_litres: eCOilLitres ? toLiters(eCOilLitres) : null,
        adblue_fill: eCAdblue, adblue_fill_km: eCAdblue ? toKm(eCAdblueKm) : null,
        window_clean: eCWindowClean, control_ok: eCControlOk, km_reading: toKm(eCKm),
        time_spent: eCTime || null, notes: eCNotes || null,
      });
    }
    setSaving(false);
    await load();
    closeOrList();
  };

  const handleDelete = async () => {
    if (!selected) return;
    await deleteEvent(selected.event_type, selected.id);
    await load();
    closeOrList();
    setConfirmingDelete(false);
  };

  // --- Inspection edit: recalc next due ---
  const calcPeriodicNextDue = (fromDate: string) => {
    const firstReg = vehicle.first_registered_on;
    if (firstReg) {
      const ageYears = (new Date(fromDate).getTime() - new Date(firstReg).getTime()) / (365.25 * 24 * 60 * 60 * 1000);
      if (ageYears < settings.firstInspectionAge) return addYears(firstReg, settings.firstInspectionAge);
    }
    return addYears(fromDate, settings.inspectionInterval);
  };

  // --- Detail row helper ---
  const Row = ({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className="grid grid-cols-2 gap-2 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm font-medium text-gray-800 ${className}`}>{value ?? '—'}</span>
    </div>
  );

  if (loading) return <div className="p-6 text-center text-gray-400 text-sm">{t('vehicles.loading')}</div>;

  // ===== LIST VIEW =====
  if (view === 'list') {
    if (!events.length) return <div className="p-6 text-center text-gray-400 text-sm">{t('events.no_events')}</div>;
    return (
      <div>
        {/* Filter + søgning */}
        <div className="px-6 pt-4 pb-2 flex gap-2">
          {availableFilters.length > 2 && (
            <select
              className="input max-w-xs"
              value={filter}
              onChange={e => setFilter(e.target.value as FilterKey)}
            >
              {availableFilters.map(k => (
                <option key={k} value={k}>{filterLabel(k)}</option>
              ))}
            </select>
          )}
          <input
            type="text"
            className="input flex-1"
            placeholder="Søg..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="divide-y">
          {(limit ? filteredEvents.slice(0, limit) : filteredEvents).map(e => (
            <div key={`${e.event_type}-${e.id}`} className="px-6 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className={`badge badge-${e.event_type}`}>{eventLabel(e)}</span>
                <span className="text-sm text-gray-500">
                  {fmt(e.date)}
                  {(e.event_type === 'checkup' || (e.event_type === 'maintenance' && e.type === 'unplanned')) && fmtTimeRange(e) && (
                    <span className="ml-1.5 text-gray-400">{fmtTimeRange(e)}</span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => handleView(e)} className="btn-link">
                  {t('events.view_event')}
                </button>
                <button onClick={() => { setSelected(e); initEdit(e); setView('edit'); }} className="btn-link">
                  {t('events.edit_event')}
                </button>
              </div>
            </div>
          ))}
        </div>
        {limit && filteredEvents.length > limit && (
          <div className="px-6 py-3 border-t text-center">
            <Link to={`/vehicles/${vehicle.id}/events`} className="text-sm text-[var(--color-primary)] hover:underline">
              {t('events.show_all', { count: filteredEvents.length })}
            </Link>
          </div>
        )}
      </div>
    );
  }

  if (!selected) return null;

  // ===== DETAIL VIEW =====
  if (view === 'detail') {
    return (
      <div className="p-6 space-y-1">
        <div className="mb-4 flex items-center gap-2">
          <span className={`badge badge-${selected.event_type}`}>{eventLabel(selected)}</span>
        </div>

        {selected.event_type === 'inspection' && (
          <>
            <Row label={t('events.date')} value={
              fmtTimeRange(selected)
                ? <>{fmt(selected.date)} <span className="text-gray-400 font-normal">kl. {fmtTimeRange(selected)}</span></>
                : fmt(selected.date)
            } />
            {selected.blocked_for_rental != null && (
              <Row label={t('events.blocked_for_rental')} value={selected.blocked_for_rental ? '✓' : '—'} />
            )}
            <Row label={t('events.inspection_type')} value={selected.inspection_type ? t(`events.inspection_type_${selected.inspection_type}`) : '—'} />
            {!!selected.km_reading && <Row label={t('events.km_reading')} value={fmtDist(selected.km_reading)} />}
            <Row
              label={t('events.result')}
              value={selected.result ? t(`events.${selected.result}`) : '—'}
              className={selected.result === 'passed' ? 'text-[var(--color-success)]' : selected.result === 'conditional' ? 'text-[var(--color-warning)]' : 'text-[var(--color-error)]'}
            />
            {selected.next_due_date && <Row label={t('events.next_due_date')} value={fmt(selected.next_due_date)} />}
            {selected.notes && <Row label={t('events.notes')} value={selected.notes} />}
            <Row label={t('events.cost')} value={selected.cost != null ? `${fmtAmount(selected.cost)} DKK` : '—'} />
            {selected.vat_status && <Row label={t('vat.full')} value={t(`vat.${selected.vat_status}`)} />}
            {selected.performed_by && <Row label={t('events.performed_by')} value={selected.performed_by} />}
            {selected.time_spent != null && <Row label={t('events.time_spent')} value={`${selected.time_spent} min`} />}
          </>
        )}

        {selected.event_type === 'maintenance' && selected.type === 'unplanned' && (
          <>
            <Row label={t('events.date')} value={
              fmtTimeRange(selected)
                ? <>{fmt(selected.date)} <span className="text-gray-400 font-normal">kl. {fmtTimeRange(selected)}</span></>
                : fmt(selected.date)
            } />
            {selected.performed_by && <Row label={t('events.delivered_by')} value={selected.performed_by} />}
            {selected.time_spent != null && <Row label={t('events.time_spent')} value={String(selected.time_spent)} />}
            {selected.cost != null && <Row label={t('events.cost')} value={`${fmtAmount(selected.cost)} DKK`} />}
            {selected.notes && <Row label={t('events.work_performed')} value={selected.notes} />}
          </>
        )}

        {selected.event_type === 'maintenance' && selected.type === 'planned' && (
          <>
            <Row label={t('events.date')} value={
              fmtTimeRange(selected)
                ? <>{fmt(selected.date)} <span className="text-gray-400 font-normal">kl. {fmtTimeRange(selected)}</span></>
                : fmt(selected.date)
            } />
            {selected.blocked_for_rental != null && (
              <Row label={t('events.blocked_for_rental')} value={selected.blocked_for_rental ? '✓' : '—'} />
            )}
            {!!selected.km_reading && <Row label={t('events.km_reading')} value={fmtDist(selected.km_reading)} />}
            {selected.notes && <Row label={t('events.notes')} value={selected.notes} />}
            <Row label={t('events.cost')} value={selected.cost != null ? `${fmtAmount(selected.cost)} DKK` : '—'} />
            {selected.vat_status && <Row label={t('vat.full')} value={t(`vat.${selected.vat_status}`)} />}
            {selected.next_due_date && <Row label={t('events.next_due_date_service')} value={fmt(selected.next_due_date)} />}
            {selected.next_due_km != null && <Row label={t('events.next_due_km')} value={fmtDist(selected.next_due_km)} />}
            {selected.performed_by && <Row label={t('events.performed_by')} value={selected.performed_by} />}
            {selected.time_spent != null && <Row label={t('events.time_spent')} value={`${selected.time_spent} min`} />}
          </>
        )}

        {selected.event_type === 'checkup' && (
          <>
            <Row label={t('events.date')} value={
              fmtTimeRange(selected)
                ? <>{fmt(selected.date)} <span className="text-gray-400 font-normal">kl. {fmtTimeRange(selected)}</span></>
                : fmt(selected.date)
            } />
            {selected.blocked_for_rental != null && (
              <Row label={t('events.blocked_for_rental')} value={selected.blocked_for_rental ? '✓' : '—'} />
            )}
            {!!selected.control_ok && <Row label={t('events.control_ok')} value="✓" />}
            {([
              'washed', 'vacuumed', 'cleaned', 'tyre_pressure',
              'washer_fluid', 'washer_fill', 'oil_checked', 'window_clean',
            ] as const).filter(k => selected[k as keyof VehicleEvent]).map(k => (
              <Row key={k} label={t(`events.${k}`)} value="✓" />
            ))}
            {!!selected.oil_added && (
              <Row label={t('events.oil_added')} value={selected.oil_added_litres != null ? `${fmtNum(litersToDisplay(selected.oil_added_litres, fuelUnit))} ${fuelLabel}` : '✓'} />
            )}
            {!!selected.km_reading && <Row label={t('events.km_reading_oil')} value={fmtDist(selected.km_reading)} />}
            {!!selected.adblue_fill && (
              <Row
                label={t('events.adblue_fill')}
                value={selected.adblue_fill_km != null ? fmtDist(selected.adblue_fill_km) : '✓'}
              />
            )}
            {selected.performed_by && <Row label={t('events.performed_by')} value={selected.performed_by} />}
            {selected.time_spent != null && <Row label={t('events.time_spent')} value={`${selected.time_spent} min`} />}
            {selected.notes && <Row label={t('events.notes')} value={selected.notes} />}
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4">
          <button onClick={closeOrList} className="text-sm text-gray-500 hover:text-gray-700">
            {onClose ? t('expense.back_to_expenses') : t('events.back_to_list')}
          </button>
          <div className="flex gap-3">
            <button onClick={handleEdit} className="btn btn-secondary">{t('events.edit_event')}</button>
            <button onClick={closeOrList} className="btn btn-primary">{t('modal.close')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ===== EDIT VIEW =====
  return (
    <div className="p-6">
      <div className="mb-4">
        <span className="reg-number">{vehicle.registration_number}</span>
        <span className="text-gray-500 text-sm ml-2">— {t('events.edit_event')}</span>
      </div>
      {selected.event_type === 'inspection' && (
        <div className="space-y-4">
          {/* Række 1: Dato + Blokeret for udlejning */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.date')}</label>
              <input type="date" className="input" value={eInsDate} onChange={e => {
                setEInsDate(e.target.value);
                if (eInsType === 'periodic' && eInsResult === 'passed') setEInsNextDue(calcPeriodicNextDue(e.target.value));
              }} />
            </div>
            <div className="flex items-end pb-2">
              <label className="checkbox-label">
                <input type="checkbox" checked={eInsBlocked} onChange={e => setEInsBlocked(e.target.checked)}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t('events.blocked_for_rental')}
              </label>
            </div>
          </div>
          {/* Række 2: Synstype + Kilometerstand */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.inspection_type')}</label>
              <select className="input" value={eInsType} onChange={e => {
                const v = e.target.value as 'periodic' | 'recheck' | 'registration';
                setEInsType(v);
                if (v === 'periodic' && eInsResult === 'passed') setEInsNextDue(calcPeriodicNextDue(eInsDate || today()));
                else if (v !== 'periodic') setEInsNextDue('');
              }}>
                <option value="periodic">{t('events.inspection_type_periodic')}</option>
                <option value="recheck">{t('events.inspection_type_recheck')}</option>
                <option value="registration">{t('events.inspection_type_registration')}</option>
              </select>
            </div>
            <div>
              <label className="label">{t('events.km_reading')} ({distLabel})</label>
              <input type="number" className="input" value={eInsKm} onChange={e => setEInsKm(e.target.value)} />
            </div>
          </div>
          {/* Række 3: Resultat + Næste syn */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.result')}</label>
              <select className="input" value={eInsResult} onChange={e => {
                const v = e.target.value as 'passed' | 'conditional' | 'failed';
                setEInsResult(v);
                if (eInsType === 'periodic') setEInsNextDue(v === 'passed' ? calcPeriodicNextDue(eInsDate || today()) : '');
              }}>
                <option value="passed">{t('events.passed')}</option>
                <option value="conditional">{t('events.conditional')}</option>
                <option value="failed">{t('events.failed')}</option>
              </select>
            </div>
            {eInsType === 'periodic' ? (
              <div>
                <label className="label">{t('events.next_due_date')}</label>
                <input type="date" className="input" value={eInsNextDue} onChange={e => setEInsNextDue(e.target.value)} />
              </div>
            ) : <div />}
          </div>
          {/* Række 4: Bemærkninger */}
          <div>
            <label className="label">{t('events.notes')}</label>
            <textarea className="input" rows={2} value={eInsNotes} onChange={e => setEInsNotes(e.target.value)} />
          </div>
          {/* Række 5: Pris */}
          <PriceInput
            label={t('events.cost')}
            amount={eInsCost}
            vatStatus={eInsVatStatus}
            vatAmount={eInsVatAmount}
            onAmountChange={setEInsCost}
            onVatStatusChange={setEInsVatStatus}
            onVatAmountChange={setEInsVatAmount}
            vatRate={settings.vatRate}
          />
          {/* Række 6: Start tid + Slut tid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.start_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eInsStartTime}
                  onChange={e => { setEInsStartTime(e.target.value); setEInsTime(calcMinutes(e.target.value, eInsEndTime)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEInsStartTime(v); setEInsTime(calcMinutes(v, eInsEndTime)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
            </div>
            <div>
              <label className="label">{t('events.end_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eInsEndTime}
                  onChange={e => { setEInsEndTime(e.target.value); setEInsTime(calcMinutes(eInsStartTime, e.target.value)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEInsEndTime(v); setEInsTime(calcMinutes(eInsStartTime, v)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
              <QuickEndTime start={eInsStartTime} onPick={(v, rest) => { setEInsEndTime(v); if (!rest) setEInsTime(calcMinutes(eInsStartTime, v)); }} />
            </div>
          </div>
          {/* Række 7: Medarbejder + Tid brugt */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.performed_by')}</label>
              <UserSelect value={eInsPerformedBy} onChange={setEInsPerformedBy} missing={employeeMissing} />
            </div>
            <div>
              <label className="label">{t('events.time_spent')}</label>
              <input type="number" className="input" value={eInsTime} onChange={e => setEInsTime(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {selected.event_type === 'maintenance' && eMType === 'unplanned' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.date')}</label>
              <input type="date" className="input" value={eMDate} onChange={e => setEMDate(e.target.value)} />
            </div>
            <div className="flex items-end pb-2">
              <label className="checkbox-label">
                <input type="checkbox" checked={eMBlocked} onChange={e => setEMBlocked(e.target.checked)}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t('events.blocked_for_rental')}
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.start_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eMStartTime}
                  onChange={e => { setEMStartTime(e.target.value); setEMTime(calcMinutes(e.target.value, eMEndTime)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEMStartTime(v); setEMTime(calcMinutes(v, eMEndTime)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
            </div>
            <div>
              <label className="label">{t('events.end_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eMEndTime}
                  onChange={e => { setEMEndTime(e.target.value); setEMTime(calcMinutes(eMStartTime, e.target.value)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEMEndTime(v); setEMTime(calcMinutes(eMStartTime, v)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
              <QuickEndTime start={eMStartTime} onPick={(v, rest) => { setEMEndTime(v); if (!rest) setEMTime(calcMinutes(eMStartTime, v)); }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.delivered_by')}</label>
              <UserSelect value={eMPerformedBy} onChange={setEMPerformedBy} missing={employeeMissing} />
            </div>
            <div>
              <label className="label">{t('events.time_spent')}</label>
              <input type="number" className="input" value={eMTime} onChange={e => setEMTime(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">{t('events.work_performed')}</label>
            <textarea className="input" rows={3} value={eMNotes} onChange={e => setEMNotes(e.target.value)} />
          </div>
          <PriceInput
            label={t('events.cost')}
            amount={eMCost}
            vatStatus={eMVatStatus}
            vatAmount={eMVatAmount}
            onAmountChange={setEMCost}
            onVatStatusChange={setEMVatStatus}
            onVatAmountChange={setEMVatAmount}
            vatRate={settings.vatRate}
          />
        </div>
      )}

      {selected.event_type === 'maintenance' && eMType === 'planned' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.date')}</label>
              <input type="date" className="input" value={eMDate} onChange={e => setEMDate(e.target.value)} />
            </div>
            <div className="flex items-end pb-2">
              <label className="checkbox-label">
                <input type="checkbox" checked={eMBlocked} onChange={e => setEMBlocked(e.target.checked)}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t('events.blocked_for_rental')}
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.start_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eMStartTime}
                  onChange={e => { setEMStartTime(e.target.value); setEMTime(calcMinutes(e.target.value, eMEndTime)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEMStartTime(v); setEMTime(calcMinutes(v, eMEndTime)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
            </div>
            <div>
              <label className="label">{t('events.end_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eMEndTime}
                  onChange={e => { setEMEndTime(e.target.value); setEMTime(calcMinutes(eMStartTime, e.target.value)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setEMEndTime(v); setEMTime(calcMinutes(eMStartTime, v)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
              <QuickEndTime start={eMStartTime} onPick={(v, rest) => { setEMEndTime(v); if (!rest) setEMTime(calcMinutes(eMStartTime, v)); }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.km_reading')} ({distLabel})</label>
              <input type="number" className="input" value={eMKm} onChange={e => setEMKm(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">{t('events.notes')}</label>
            <textarea className="input" rows={2} value={eMNotes} onChange={e => setEMNotes(e.target.value)} />
          </div>
          <PriceInput
            label={t('events.cost')}
            amount={eMCost}
            vatStatus={eMVatStatus}
            vatAmount={eMVatAmount}
            onAmountChange={setEMCost}
            onVatStatusChange={setEMVatStatus}
            onVatAmountChange={setEMVatAmount}
            vatRate={settings.vatRate}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.next_due_date_service')}</label>
              <input type="date" className="input" value={eMNextDate} onChange={e => setEMNextDate(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('events.next_due_km')} ({distLabel})</label>
              <input type="number" className="input" value={eMNextKm} onChange={e => setEMNextKm(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.performed_by')}</label>
              <UserSelect value={eMPerformedBy} onChange={setEMPerformedBy} missing={employeeMissing} />
            </div>
            <div>
              <label className="label">{t('events.time_spent')}</label>
              <input type="number" className="input" value={eMTime} onChange={e => setEMTime(e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {selected.event_type === 'checkup' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.date')}</label>
              <input type="date" className="input" value={eCDate} onChange={e => setECDate(e.target.value)} />
            </div>
            <div className="flex items-end pb-2">
              <label className="checkbox-label">
                <input type="checkbox" checked={eCBlocked} onChange={e => setECBlocked(e.target.checked)}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t('events.blocked_for_rental')}
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.start_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eCStartTime}
                  onChange={e => { setECStartTime(e.target.value); setECTime(calcMinutes(e.target.value, eCEndTime)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setECStartTime(v); setECTime(calcMinutes(v, eCEndTime)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
            </div>
            <div>
              <label className="label">{t('events.end_time')}</label>
              <div className="flex gap-2">
                <input type="time" className="input" value={eCEndTime}
                  onChange={e => { setECEndTime(e.target.value); setECTime(calcMinutes(eCStartTime, e.target.value)); }}
                  onWheel={e => e.currentTarget.blur()} />
                <button type="button" onClick={() => { const v = new Date().toTimeString().slice(0, 5); setECEndTime(v); setECTime(calcMinutes(eCStartTime, v)); }}
                  className="btn btn-secondary shrink-0 px-2 py-1 text-xs">{t('events.now')}</button>
              </div>
              <QuickEndTime start={eCStartTime} onPick={(v, rest) => { setECEndTime(v); if (!rest) setECTime(calcMinutes(eCStartTime, v)); }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {([
              { key: 'washed',        val: eCWashed,      set: setECWashed },
              { key: 'vacuumed',      val: eCVacuumed,    set: setECVacuumed },
              { key: 'cleaned',       val: eCCleaned,     set: setECCleaned },
              { key: 'window_clean',  val: eCWindowClean, set: setECWindowClean },
              { key: 'tyre_pressure', val: eCTyre,        set: setECTyre },
              { key: 'washer_fluid',  val: eCWasherFluid, set: setECWasherFluid },
              { key: 'washer_fill',   val: eCWasherFill,  set: setECWasherFill },
              { key: 'oil_checked',   val: eCOilChecked,  set: setECOilChecked },
              { key: 'oil_added',     val: eCOilAdded,    set: setECOilAdded },
              { key: 'adblue_fill',   val: eCAdblue,      set: setECAdblue },
            ] as const).map(item => (
              <label key={item.key} className="checkbox-label">
                <input type="checkbox" checked={item.val}
                  onChange={e => { item.set(e.target.checked); if (e.target.checked) setECControlOk(false); }}
                  className="rounded border-gray-300 text-[var(--color-primary)]" />
                {t(`events.${item.key}`)}
              </label>
            ))}
          </div>

          {/* Stand-vurdering (1-10) */}
          <div className="border-t pt-3 space-y-2">
            <p className="label">Bilens stand (1-10)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Udvendigt</span>
                  <span className="text-sm font-medium" style={{ minWidth: 24 }}>{eCExtCond || '—'}</span>
                </div>
                <input type="range" min={1} max={10} step={1}
                  value={eCExtCond || 5}
                  onChange={e => setECExtCond(parseInt(e.target.value, 10))}
                  className="w-full" />
                {!eCExtCond ? (
                  <button type="button" onClick={() => setECExtCond(5)} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                    Klik for at vurdere
                  </button>
                ) : (
                  <button type="button" onClick={() => setECExtCond('')} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                    Ryd
                  </button>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Indvendigt</span>
                  <span className="text-sm font-medium" style={{ minWidth: 24 }}>{eCIntCond || '—'}</span>
                </div>
                <input type="range" min={1} max={10} step={1}
                  value={eCIntCond || 5}
                  onChange={e => setECIntCond(parseInt(e.target.value, 10))}
                  className="w-full" />
                {!eCIntCond ? (
                  <button type="button" onClick={() => setECIntCond(5)} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                    Klik for at vurdere
                  </button>
                ) : (
                  <button type="button" onClick={() => setECIntCond('')} className="text-xs text-gray-400 hover:text-gray-600 mt-0.5">
                    Ryd
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Hurtig kontrol — fjerner øvrige valg og sætter tidsforbrug til 5 min */}
          <label className="checkbox-label font-medium border-t pt-3">
            <input type="checkbox" checked={eCControlOk} onChange={e => applyECControlOk(e.target.checked)}
              className="rounded border-gray-300 text-[var(--color-primary)]" />
            {t('events.control_ok')}
          </label>
          {eCOilAdded && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">{t('events.oil_added_litres')} ({fuelLabel})</label>
                <input type="number" step="0.1" className="input" value={eCOilLitres} onChange={e => setECOilLitres(e.target.value)} />
              </div>
              <div>
                <label className="label">{t('events.km_reading_oil')} ({distLabel})</label>
                <input type="number" className="input" value={eCKm} onChange={e => setECKm(e.target.value)} />
              </div>
            </div>
          )}
          {eCAdblue && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">{t('events.adblue_fill_km')} ({distLabel})</label>
                <input type="number" className="input" value={eCAdblueKm} onChange={e => setECAdblueKm(e.target.value)} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('events.performed_by')}</label>
              <UserSelect value={eCBy} onChange={setECBy} missing={employeeMissing} />
            </div>
            <div>
              <label className="label">{t('events.time_spent')}</label>
              <input type="number" className="input" value={eCTime} onChange={e => setECTime(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label">{t('events.notes')}</label>
            <textarea className="input" rows={2} value={eCNotes} onChange={e => setECNotes(e.target.value)} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4">
        {confirmingDelete ? (
          <div className="flex items-center gap-3 w-full">
            <span className="text-sm text-gray-700 flex-1">{t('events.confirm_delete')}</span>
            <button onClick={() => setConfirmingDelete(false)} className="btn btn-secondary text-sm">
              {t('modal.cancel')}
            </button>
            <button onClick={handleDelete} className="btn btn-danger text-sm">
              {t('events.delete_confirm_yes')}
            </button>
          </div>
        ) : (
          <>
            <button onClick={() => setConfirmingDelete(true)} className="btn btn-danger-outline text-sm">
              {t('events.delete_event')}
            </button>
            <div className="flex items-center gap-3">
              <button onClick={() => setView('detail')} className="btn btn-secondary">
                {t('modal.cancel')}
              </button>
              <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? '...' : t('modal.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
